import { useEffect, useId, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { hapticFeedback } from '@/lib/feedback';
import {
  barcodeFieldCameraScanConfig,
  barcodeFieldHtml5QrcodeConfig,
} from '@/lib/activity-field-scanner';
import { safeStopHtml5Qrcode } from '@/lib/html5-qrcode-scanner';

interface FieldBarcodeScannerDialogProps {
  open: boolean;
  fieldLabel: string;
  onOpenChange: (open: boolean) => void;
  onCapture: (value: string) => void;
}

interface FieldBarcodeScannerViewportProps {
  containerId: string;
  onCapture: (value: string) => void;
}

function FieldBarcodeScannerViewport({ containerId, onCapture }: FieldBarcodeScannerViewportProps) {
  const [isReady, setIsReady] = useState(false);
  const lastCaptureRef = useRef<string | null>(null);
  const lastCaptureTimeRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    let scanner: Html5Qrcode | null = null;
    lastCaptureRef.current = null;
    lastCaptureTimeRef.current = 0;

    const startScanner = async () => {
      if (!document.getElementById(containerId)) {
        if (isMounted) window.setTimeout(() => void startScanner(), 100);
        return;
      }

      try {
        scanner = new Html5Qrcode(containerId, barcodeFieldHtml5QrcodeConfig);
        await scanner.start(
          { facingMode: 'environment' },
          barcodeFieldCameraScanConfig,
          (decodedText) => {
            const now = Date.now();
            if (decodedText === lastCaptureRef.current && now - lastCaptureTimeRef.current < 2000) {
              return;
            }
            lastCaptureRef.current = decodedText;
            lastCaptureTimeRef.current = now;
            hapticFeedback.qrDetected();
            onCapture(decodedText);
          },
          () => {
            //
          }
        );
        if (!isMounted) {
          await safeStopHtml5Qrcode(scanner);
          scanner = null;
          return;
        }
        setIsReady(true);
      } catch (error) {
        console.error('Failed to start barcode field scanner', error);
        await safeStopHtml5Qrcode(scanner);
        scanner = null;
      }
    };

    const timer = window.setTimeout(() => void startScanner(), 150);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
      const instance = scanner;
      scanner = null;
      void safeStopHtml5Qrcode(instance);
    };
  }, [containerId, onCapture]);

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-black ring-1 ring-border">
      <div
        id={containerId}
        data-html5-camera-container
        className="absolute inset-0 h-full w-full min-h-0"
      />
      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
        <div className="h-[34%] w-[92%] rounded-xl border-2 border-primary/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]" />
      </div>
      {!isReady && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/85 text-zinc-100">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-sm">Starting camera…</p>
        </div>
      )}
    </div>
  );
}

export function FieldBarcodeScannerDialog({
  open,
  fieldLabel,
  onOpenChange,
  onCapture,
}: FieldBarcodeScannerDialogProps) {
  const reactId = useId();
  const containerId = `activity-barcode-field-scan-${reactId.replace(/:/g, '')}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-4 p-0 overflow-hidden z-[100]">
        <DialogHeader className="px-6 pt-6 pb-0 text-center sm:text-center">
          <DialogTitle>Scan {fieldLabel}</DialogTitle>
          <DialogDescription>
            Align the barcode horizontally inside the frame, or center a QR code.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6">
          {open ? (
            <FieldBarcodeScannerViewport containerId={containerId} onCapture={onCapture} />
          ) : null}
        </div>

        <div className="flex justify-end border-t border-border px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
